import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../../components/ui/button';
import api from '../../lib/api';
import { toast } from 'sonner';
import {
    Check, X, User, Mail, Phone, ArrowRight, Shield, Clock, Sparkles
} from 'lucide-react';

// Logo Component
function HomlyLogo({ size = 'md' }) {
    const sizes = { sm: 'text-xl', md: 'text-2xl', lg: 'text-4xl' };
    return (
        <div className={`font-black ${sizes[size]} flex items-center gap-0.5`}>
            <span className="bg-gradient-to-r from-[#681793] to-[#FF914D] bg-clip-text text-transparent">Homly</span>
            <span className="text-[#FF914D]">.</span>
        </div>
    );
}

export default function RespondProposalPage() {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    const autoAccept = searchParams.get('accept') === 'true';
    
    const [proposal, setProposal] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [responseStatus, setResponseStatus] = useState(null);
    
    const [formData, setFormData] = useState({
        vendor_first_name: '',
        vendor_email: '',
        vendor_phone: ''
    });
    
    useEffect(() => {
        if (token) {
            checkProposal();
        } else {
            setLoading(false);
        }
    }, [token]);
    
    const checkProposal = async () => {
        try {
            // We need to fetch proposal details from backend
            // For now, we'll show the form directly since the token is validated on submit
            setProposal({ token });
        } catch (err) {
            toast.error('Proposition non trouvée ou expirée');
        } finally {
            setLoading(false);
        }
    };
    
    const handleSubmit = async (accepted) => {
        if (!formData.vendor_first_name || !formData.vendor_email) {
            toast.error('Veuillez remplir les champs obligatoires');
            return;
        }
        
        setSubmitting(true);
        try {
            const res = await api.post('/reseau/visitors/respond-proposal', {
                proposal_token: token,
                accepted,
                vendor_email: formData.vendor_email,
                vendor_first_name: formData.vendor_first_name,
                vendor_phone: formData.vendor_phone || null
            });
            
            setSubmitted(true);
            setResponseStatus(accepted ? 'accepted' : 'rejected');
            
            if (accepted) {
                toast.success('Contact accepté ! L\'agent vous contactera bientôt.');
            } else {
                toast.info('Proposition refusée.');
            }
        } catch (err) {
            const detail = err.response?.data?.detail || 'Une erreur est survenue';
            toast.error(detail);
        } finally {
            setSubmitting(false);
        }
    };
    
    if (loading) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="w-8 h-8 border-2 border-[#681793] border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }
    
    if (!token) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center p-4">
                <div className="text-center">
                    <h2 className="text-2xl font-bold text-white mb-4">Lien invalide</h2>
                    <p className="text-white/50 mb-6">Ce lien de proposition n'est pas valide.</p>
                    <Link to="/reseau">
                        <Button className="bg-gradient-to-r from-[#681793] to-[#FF914D]">
                            Retour au réseau
                        </Button>
                    </Link>
                </div>
            </div>
        );
    }
    
    if (submitted) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center p-4">
                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="max-w-md w-full text-center"
                >
                    <div className={`w-20 h-20 rounded-2xl ${responseStatus === 'accepted' ? 'bg-green-500/20' : 'bg-white/5'} flex items-center justify-center mx-auto mb-6`}>
                        {responseStatus === 'accepted' ? (
                            <Check className="w-10 h-10 text-green-400" />
                        ) : (
                            <X className="w-10 h-10 text-white/40" />
                        )}
                    </div>
                    
                    <h2 className="text-2xl font-bold text-white mb-2">
                        {responseStatus === 'accepted' ? 'Contact accepté !' : 'Proposition refusée'}
                    </h2>
                    
                    <p className="text-white/50 mb-8">
                        {responseStatus === 'accepted' 
                            ? 'L\'agent a été notifié et vous contactera dans les 24-48h.'
                            : 'Vous ne serez pas contacté par cet agent.'
                        }
                    </p>
                    
                    <Link to="/reseau">
                        <Button className="bg-gradient-to-r from-[#681793] to-[#FF914D]">
                            Découvrir d'autres agents
                            <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                    </Link>
                </motion.div>
            </div>
        );
    }
    
    return (
        <div className="min-h-screen bg-background text-white">
            {/* Header */}
            <nav className="bg-background/80 backdrop-blur-xl border-b border-white/5">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        <Link to="/reseau">
                            <HomlyLogo size="md" />
                        </Link>
                    </div>
                </div>
            </nav>
            
            {/* Main content */}
            <div className="max-w-lg mx-auto px-4 py-12">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="space-y-8"
                >
                    {/* Header */}
                    <div className="text-center">
                        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#681793] to-[#FF914D] flex items-center justify-center mx-auto mb-4">
                            <Sparkles className="w-8 h-8 text-white" />
                        </div>
                        <h1 className="text-2xl font-bold text-white mb-2">
                            Un agent souhaite vous contacter
                        </h1>
                        <p className="text-white/50">
                            Acceptez pour partager vos coordonnées et être contacté.
                        </p>
                    </div>
                    
                    {/* Form */}
                    <div className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 p-6">
                        <h3 className="text-lg font-semibold text-white mb-4">
                            Vos coordonnées
                        </h3>
                        
                        <div className="space-y-4">
                            <div>
                                <label className="text-sm text-white/60 mb-1 block">
                                    <User className="w-4 h-4 inline mr-1" />
                                    Prénom *
                                </label>
                                <input
                                    value={formData.vendor_first_name}
                                    onChange={(e) => setFormData({ ...formData, vendor_first_name: e.target.value })}
                                    placeholder="Jean"
                                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-white/30 focus:border-[#681793] focus:outline-none transition-colors"
                                    required
                                />
                            </div>
                            
                            <div>
                                <label className="text-sm text-white/60 mb-1 block">
                                    <Mail className="w-4 h-4 inline mr-1" />
                                    Email *
                                </label>
                                <input
                                    type="email"
                                    value={formData.vendor_email}
                                    onChange={(e) => setFormData({ ...formData, vendor_email: e.target.value })}
                                    placeholder="jean@email.com"
                                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-white/30 focus:border-[#681793] focus:outline-none transition-colors"
                                    required
                                />
                            </div>
                            
                            <div>
                                <label className="text-sm text-white/60 mb-1 block">
                                    <Phone className="w-4 h-4 inline mr-1" />
                                    Téléphone (optionnel)
                                </label>
                                <input
                                    value={formData.vendor_phone}
                                    onChange={(e) => setFormData({ ...formData, vendor_phone: e.target.value })}
                                    placeholder="06 12 34 56 78"
                                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-white/30 focus:border-[#681793] focus:outline-none transition-colors"
                                />
                            </div>
                        </div>
                        
                        {/* Trust indicators */}
                        <div className="flex items-center gap-4 mt-6 pt-4 border-t border-white/10">
                            <div className="flex items-center gap-1 text-xs text-white/40">
                                <Shield className="w-3 h-3 text-green-400" />
                                Données sécurisées
                            </div>
                            <div className="flex items-center gap-1 text-xs text-white/40">
                                <Clock className="w-3 h-3 text-[#FF914D]" />
                                Contact sous 48h
                            </div>
                        </div>
                    </div>
                    
                    {/* Action buttons */}
                    <div className="space-y-3">
                        <Button
                            onClick={() => handleSubmit(true)}
                            disabled={submitting}
                            className="w-full h-14 bg-gradient-to-r from-[#681793] to-[#FF914D] hover:opacity-90 text-lg font-semibold"
                        >
                            {submitting ? 'Envoi...' : 'Accepter le contact'}
                            <Check className="w-5 h-5 ml-2" />
                        </Button>
                        
                        <Button
                            onClick={() => handleSubmit(false)}
                            disabled={submitting}
                            variant="outline"
                            className="w-full border-white/10 text-white/60 hover:text-white hover:bg-white/5"
                        >
                            Refuser
                        </Button>
                    </div>
                    
                    <p className="text-center text-xs text-white/30">
                        En acceptant, vous autorisez l'agent à vous contacter par email et/ou téléphone pour discuter de votre projet immobilier.
                    </p>
                </motion.div>
            </div>
        </div>
    );
}
